import { isString } from "lodash";

/**
 * Parse settings object - handles both JSON string and plain object
 * @param {Object|String} settings - Settings data
 * @returns {Object} Parsed settings object
 */
const parseSettings = settings => {
  if (!settings) return {};

  if (isString(settings)) {
    try {
      return JSON.parse(settings);
    } catch (error) {
      console.error("Error parsing settings:", error);
      return {};
    }
  }

  return settings;
};

/**
 * Get location payload from location object
 * @param {Object} location - Location object
 * @param {Boolean} isCreate - Whether this is for create mutation (default: false for update)
 * @returns {Object} Location payload with all required fields
 */
export const getLocationPayload = (location, isCreate = false) => {
  if (!location) {
    throw new Error("Location object is required");
  }

  // Parse settings (handle both JSON string and object)
  const settings = parseSettings(location.settings);

  // Top-level args (common for both create and update)
  const topLevelArgs = {
    regionId: parseInt(location.regionId),
    addressLine1: location.addressLine1 || "",
    addressLine2: location.addressLine2 || "",
    city: location.city || "",
    country: location.country || "",
    email: location.email || "",
    geoLink: location.geoLink || "",
    inventoryUrlFormat: location.inventoryUrlFormat || "",
    isActive: Boolean(location.isActive),
    isDefault: Boolean(location.isDefault),
    locationType: location.locationType || "",
    notes: location.notes || "",
    phone: location.phone || "",
    postalCode: location.postalCode || "",
    state: location.state || "",
    timezone: location.timezone || "",
    title: location.title || "",
    website: location.website || "",
    opportunityTypeId: location.opportunityTypeId || location?.opportunityType?.id || null,
  };

  // Add specific fields based on create vs update
  if (isCreate) {
    // For create mutation, add companyId at top level
    topLevelArgs.companyId = parseInt(location.companyId);
  } else {
    // For update mutation, add locationId at top level
    topLevelArgs.locationId = parseInt(location.locationId || location.id);
  }

  // Settings object (everything else goes here)
  // Remove NOTES from settings by destructuring
  const { NOTES, ...settingsWithoutNotes } = settings || {};
  const settingsObject = {
    ...settingsWithoutNotes, // Keep existing settings except NOTES
    OPENAI_API_KEY: location.openaiApiKey || settings.OPENAI_API_KEY || "",
    LATITUDE: location.latitude || settings.LATITUDE || null,
    LONGITUDE: location.longitude || settings.LONGITUDE || null,
    MFA_ENABLED: location.isMfaEnabled ?? location.enableMFA ?? Boolean(settings.MFA_ENABLED),
    MFA_METHODS: location.mfaMethods || settings.MFA_METHODS || [],
    DISPLAY_NAME: location.displayName || settings.DISPLAY_NAME || "",
    LOCATION_LOGO: location.locationLogo || settings.LOCATION_LOGO || "",
    SERVICE_PHONE: location.servicePhone || settings.SERVICE_PHONE || "",
    REPLY_TO_EMAIL: location.replyToEmail || settings.REPLY_TO_EMAIL || "",
    FROM_EMAIL: location.fromEmail || settings.FROM_EMAIL || "",
    PREFERRED_LANGUAGE: location.preferredLanguage || settings.PREFERRED_LANGUAGE || "",
    PRICE_ALERT_ENABLED: Boolean(location.priceAlertEnabled ?? settings.PRICE_ALERT_ENABLED),
    // [EAI-1337] AUTO_RESPONSE_CAMPAIGN key is already replaced by the AUTO_RESPONSE_ENABLED setting, so it is currently unused.
    // AUTO_RESPONSE_CAMPAIGN:
    //   location.autoResponseCampaign ?? Boolean(settings.AUTO_RESPONSE_CAMPAIGN),
    is_email_link_shortened: Boolean(
      location.isEmailLinkShortened ?? settings.is_email_link_shortened
    ),
    FOLLOW_UP_LOST_LEAD_ENABLED: Boolean(
      location.followUpLostLeadEnabled ?? settings.FOLLOW_UP_LOST_LEAD_ENABLED
    ),
    AI_APPOINTMENT_MANAGEMENT_ENABLED: Boolean(
      location.aiAppointmentManagerEnabled ?? settings.AI_APPOINTMENT_MANAGEMENT_ENABLED
    ),
    AUTO_RESPONSE_ENABLED: Boolean(location.autoResponseEnabled ?? settings.AUTO_RESPONSE_ENABLED),
    IS_PUSH_TO_CRM: Boolean(location.isPushToCrm ?? settings.IS_PUSH_TO_CRM),
    IS_PUSH_TO_DMS: Boolean(location.isPushToDms ?? settings.IS_PUSH_TO_DMS),
    DISABLE_TRANSLATION: Boolean(location.disableTranslation ?? settings.DISABLE_TRANSLATION),
    SHOULD_SHOW_DEPARTMENT_FIELD_IN_LEAD_AND_OPPORTUNITY: Boolean(
      location.renderDepartmentOnOpportunityForm ??
        settings.SHOULD_SHOW_DEPARTMENT_FIELD_IN_LEAD_AND_OPPORTUNITY
    ),
    QUICK_LEAD_OPPORTUNITY_TYPE_ENABLED: Boolean(
      location.quickLeadOpportunityTypeEnabled ?? settings.QUICK_LEAD_OPPORTUNITY_TYPE_ENABLED
    ),
    ALLOW_TENTATIVE_APPOINTMENT_TIME: Boolean(
      location.allowTentativeAppointmentTime ?? settings.ALLOW_TENTATIVE_APPOINTMENT_TIME
    ),
    AUTO_RESPONSE_FEEDBACK: Boolean(
      location.isAutoResponderFeedbackEnabled ?? settings.AUTO_RESPONSE_FEEDBACK
    ),
    GENERAL_CONFIG: {
      ...(settings.GENERAL_CONFIG || {}),
      SHOW_ACTIVATE_USER_ONLY: Boolean(
        location.activeUserRendering ?? settings.GENERAL_CONFIG?.SHOW_ACTIVATE_USER_ONLY
      ),
      ALLOW_ACTIVE_MULTIPLE_OPPORTUNITY: Boolean(
        location.allowDuplicateOpportunity ??
          settings.GENERAL_CONFIG?.ALLOW_ACTIVE_MULTIPLE_OPPORTUNITY
      ),
    },
  };

  return {
    ...topLevelArgs,
    settings: settingsObject,
  };
};
